import React, {Fragment, memo, useEffect, useState} from "react";
import {orderBy} from "lodash";
import PropTypes from "prop-types";
import {Row} from "reactstrap";
import {FaPencil} from "react-icons/fa6";
import {useMediaQuery} from "react-responsive";
import {useDispatch, useSelector} from "react-redux";
import Toggle from "react-toggle";
import ProtectedContent, {usePermissionCheck} from "../../../components/protectedContent";
import ListView from "../../../components/ContentDisplay/ListView";
import EditEthernetConfig from "../../../components/Modal/EditEthernetConfig";
import ConfirmModal from "../../../components/Modal/ConfirmModal";
import {CONTROLLER} from "../../../constants/appConstants";
import {
  requestEthernetConfig,
  requestPortFwdConfig,
  requestEditEthernetConfigurations,
  requestEditPortForwardingConfigurations,
  requestModbusTcpIp,
  resetPFConfig,
  saveModbusTcpIp,
  changeEmailTransmissionConfig,
  requestCellularModem,
  saveCellularModem,
} from "../../../redux/Actions/toolsTab.action";
import {getSelectedController} from "../../../redux/Selectors/controllers.selectors";
import {
  getEthernetConfigurationsNew,
  getModbusTcpIp,
  getCellularModem,
  portForwardingStatus,
  portForwardingTableData,
  emailTransmissionStatus,
} from "../../../redux/Selectors/toolsTab.selector";
import {getUserProfile} from "../../../redux/Selectors/users.selector";
import PortForwardingCard from "./portForwardingCard";
import EthernetConfigCard from "./ethernetConfigCard";
import AquaticProfiles from "./aquaticProfile";
import "./toolsStyles.scss";

const Tools = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [configObject, setConfigObject] = useState(undefined);
  const [cellularModemConfirmOpen, setCellularModemConfirmOpen] = useState(false);
  const [pendingCellularModem, setPendingCellularModem] = useState(false);
  const isMobileOrTablet = useMediaQuery({query: "(max-width: 1080px)"});
  const dispatch = useDispatch();
  const ethernetConfigs = useSelector(getEthernetConfigurationsNew);
  const statusOfPF = useSelector(portForwardingStatus);
  const statusOfModbus = useSelector(getModbusTcpIp);
  const statusOfCellularModem = useSelector(getCellularModem);
  const emailTransmission = useSelector(emailTransmissionStatus);
  const postForwardingConfig = useSelector(portForwardingTableData);
  const userProfile = useSelector(getUserProfile);

  const selectedController = useSelector(getSelectedController);
  const {category_name, properties = {}} = selectedController;
  const {appliance_type = ""} = properties;

  const showEmailTransmission =
    appliance_type === "Loki1" ||
    appliance_type === "Loki2" ||
    appliance_type === "Loki3" ||
    appliance_type === "Loki4";

  // const canChangeInventory = usePermissionCheck(["change_systeminventory"]);
  const canAddPF = usePermissionCheck(["add_portforwarding"]);

  useEffect(() => {
    dispatch(requestEthernetConfig());
    dispatch(requestModbusTcpIp());
    dispatch(requestCellularModem());
  }, [dispatch]);

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setConfigObject(undefined);
  };

  const handleModalSubmit = (submitObj) => {
    const ethernetObject = {
      ...configObject,
      field_definition: submitObj,
    };
    if (configObject.configType === "ethernet") {
      dispatch(requestEditEthernetConfigurations(ethernetObject, handleModalClose));
    } else {
      const callback = () => {
        handleModalClose();
        dispatch(requestPortFwdConfig());
      };
      dispatch(requestEditPortForwardingConfigurations(ethernetObject, callback));
    }
  };

  const handlePFStatusChange = (e) => {
    const {checked} = e.target;
    dispatch(resetPFConfig(checked));
    if (checked) {
      dispatch(requestPortFwdConfig());
    }
  };

  const handleModbusStatusChange = (e) => {
    const {checked} = e.target;
    dispatch(saveModbusTcpIp({enable: checked}));
  };

  const handleCellularModemChange = (e) => {
    const {checked} = e.target;
    setPendingCellularModem(checked);
    setCellularModemConfirmOpen(true);
  };

  const handleCellularModemConfirm = () => {
    dispatch(saveCellularModem({enable: pendingCellularModem}));
    setCellularModemConfirmOpen(false);
  };

  const handleEmailTransmissionChange = (e) => {
    const {checked} = e.target;
    dispatch(changeEmailTransmissionConfig({enable: checked}));
  };

  return (
    <Fragment>
      {configObject && modalOpen && (
        <EditEthernetConfig
          open={modalOpen}
          configObj={orderBy(configObject.field_definition, ["field_order"], ["asc"])}
          onSubmit={handleModalSubmit}
          onClose={handleModalClose}
        />
      )}
      <div className={"tools-container"}>
        {/*Ethernet */}
        {ethernetConfigs?.length > 0 && (
          <div>
            <div className={"sectionLabelSpacing sectionLabel roboto-bold"}>
              {CONTROLLER.TOOLS.ETHERNET}
            </div>
            <Row style={{margin: "10px"}}>
              {ethernetConfigs.map((eachConfig, index) => {
                return (
                  <EthernetConfigCard
                    key={index}
                    config={eachConfig}
                    onEdit={() => {
                      setModalOpen(true);
                      setConfigObject(eachConfig);
                    }}
                  />
                );
              })}
            </Row>
          </div>
        )}
        {/*TCP/IP */}
        {statusOfModbus !== null && (
          <div className={"sectionLabelSpacing"}>
            <span className={"sectionLabel roboto-bold"}>{CONTROLLER.TOOLS.MODBUS}</span>
            {/*TODO: need to change permission for enable/disable TCP/IP */}
            <div data-cy={"modbus-status"}>
              <Toggle
                checked={statusOfModbus}
                onChange={handleModbusStatusChange}
                // disabled={!canChangeInventory}
              />
            </div>
          </div>
        )}
        {/*Cellular Modem */}
        {(userProfile.is_staff || userProfile.is_superuser) && (
          <div className={"sectionLabelSpacing"}>
            <ConfirmModal
              open={cellularModemConfirmOpen}
              header={CONTROLLER.TOOLS.CELLULAR_MODEM}
              modalBody={`Are you sure you want to ${pendingCellularModem ? "enable" : "disable"} Cellular Modem?`}
              onConfirm={handleCellularModemConfirm}
              onClose={() => setCellularModemConfirmOpen(false)}
            />
            <span className={"sectionLabel roboto-bold"}>
              {CONTROLLER.TOOLS.CELLULAR_MODEM}
            </span>
            <div data-cy={"cellular-modem-status"}>
              <Toggle
                checked={statusOfCellularModem}
                onChange={handleCellularModemChange}
              />
            </div>
          </div>
        )}
        {/*Port Forwarding */}
        {!!postForwardingConfig && (
          <ProtectedContent perms={["view_portforwarding"]}>
            <Fragment>
              <div className={"sectionLabelSpacing"}>
                <span className={"sectionLabel roboto-bold"}>
                  {CONTROLLER.TOOLS.PORT_FORWARDING}
                </span>
                <div data-cy={"PF-status"}>
                  <Toggle
                    checked={statusOfPF}
                    onChange={handlePFStatusChange}
                    disabled={!canAddPF}
                  />
                </div>
              </div>
              {statusOfPF && (
                <div style={{margin: isMobileOrTablet ? "10px" : "20px"}}>
                  <ListView
                    withActions={
                      postForwardingConfig.headers &&
                      postForwardingConfig.headers.length > 0
                    }
                    actionsList={[
                      {label: "Edit", icon: FaPencil, perms: ["change_portforwarding"]},
                    ]}
                    permsForActions={["change_portforwarding"]}
                    listItems={postForwardingConfig.data}
                    listHeaders={postForwardingConfig.headers}
                    onSelectAction={(action, row) => {
                      if (action === "Edit") {
                        handleModalOpen();
                        setConfigObject(row.config);
                      }
                    }}
                    cardContent={PortForwardingCard}
                    tableName={"port-forwarding"}
                  />
                </div>
              )}
            </Fragment>
          </ProtectedContent>
        )}
        {showEmailTransmission && (
          <div className={"sectionLabelSpacing"}>
            <span className={"sectionLabel roboto-bold"}>
              {CONTROLLER.TOOLS.EMAIL_TRANSMISSION}
            </span>
            <div data-cy={"email-transmission"}>
              <Toggle
                checked={emailTransmission}
                onChange={handleEmailTransmissionChange}
              />
            </div>
          </div>
        )}
        {category_name === "aquatic" && (
          <ProtectedContent perms={["view_aquaticgroupprofile"]}>
            <AquaticProfiles />
          </ProtectedContent>
        )}
      </div>
    </Fragment>
  );
};

Tools.propTypes = {
  items: PropTypes.array,
};

export default memo(Tools);
